import { useMemo, forwardRef, useId } from 'react';
import { Box } from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';

import { RenderComponentProps } from '../@types';
import i18n from '../translations/i18n';
import { nameToClass } from '../utils/css';

import { InputControl } from './ui/controls/InputControl';

type TransformerResponse = {
  Component: React.FC<any>;
  props: Record<string, any>;
};

export const transformDataForRenderComponent = ({
  field,
  config,
}: RenderComponentProps): TransformerResponse | null => {
  if (!field || !config) return null;

  const t = i18n.t;
  const { component, label, id, name, defaultValue, list, className, scheme } =
    field;

  const { required: isRequired = false } = config || {};

  const Component = component || InputControl;

  const props = {
    isRequired,
    label: t(label),
    name,
    ...(id && { id }),
    ...(defaultValue && { defaultValue }),
    ...(className && { className }),
    ...(list && { list }),
    ...(scheme && { scheme }),
  };

  return {
    Component,
    props,
  };
};

export const RenderComponent = forwardRef<any, any>(
  ({ field, config, ...renderComponentProps }: RenderComponentProps, ref) => {
    const key = useId();

    const { Component, props } = useMemo(
      () =>
        transformDataForRenderComponent({ field, config }) ||
        ({} as TransformerResponse),
      [field, config],
    );

    if (!Component || !props || !field) return null;

    const { className = 'LongTextControl', ...otherProps } = props;

    return (
      <Box
        className={cx(
          'FormControlContainer',
          nameToClass(field.name, 'container'),
          className,
        )}
      >
        <Component
          key={key}
          ref={ref}
          {...otherProps}
          {...renderComponentProps}
        />
      </Box>
    );
  },
);
